import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/apiClient";
import useAutoRefresh from "@/lib/useAutoRefresh";
import useWallboardConfig from "@/lib/wallboardConfig";
import { useViewMode } from "@/lib/ViewModeContext";
import { STEPS, isOrderCompleted, isOrderCancelled } from "@/lib/stages";
import { fetchOrdersWithItems } from "@/lib/fetchOrdersWithItems";
import CDLogo from "@/components/CDLogo";
import GoalRing from "@/components/wallboard/GoalRing";
import WallboardSettings from "@/components/wallboard/WallboardSettings";
import {
  Package, CheckCircle, Hammer, ShieldCheck, Truck, Storefront, PaintBrush,
  Pulse, Trophy, ArrowSquareOut, X, GearSix,
} from "@phosphor-icons/react";

/**
 * Full-screen, dark, big-format operations wallboard.
 * Renders OUTSIDE AppLayout (no sidebar). Auto-refreshes silently every 30s.
 * Designed to be cast on a TV in the shop. Admin-only via ProtectedRoute in App.js.
 */
export default function Wallboard() {
  const [orders, setOrders] = useState([]);
  const [now, setNow] = useState(new Date());
  const [settingsOpen, setSettingsOpen] = useState(false);
  const { viewMode } = useViewMode() || {};
  const wb = useWallboardConfig();
  const { config } = wb;

  const fetchOrders = useCallback(async () => {
    // List endpoint omits items[]; enrich via parallel detail fetches so the
    // per-stage counts, leaderboard, and ticker all have scan-event data.
    const enriched = await fetchOrdersWithItems();
    setOrders(enriched);
  }, []);

  const { refresh, lastUpdatedAt } = useAutoRefresh(fetchOrders, {
    intervalMs: Math.max(5, config.refreshSec) * 1000,
    initialPolling: true,
  });

  // Live clock
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  // Request fullscreen on first click anywhere (browsers block automatic fullscreen)
  const goFullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen?.();
    else document.documentElement.requestFullscreen?.().catch(() => {});
  };

  const events = useMemo(() => flattenEvents(orders), [orders]);
  const todayStart = startOfToday().getTime();
  const todaysEvents = events.filter((e) => new Date(e.at).getTime() >= todayStart);

  const scansToday = todaysEvents.reduce((sum, e) => sum + (e.qty || 1), 0);
  const shippedToday = todaysEvents
    .filter((e) => e.step === "Shipped")
    .reduce((sum, e) => sum + (e.qty || 1), 0);

  const stageCounts = useMemo(() => {
    const c = { New: 0, Building: 0, QA: 0, Shipped: 0, Cancelled: 0 };
    orders.forEach((o) => {
      if (isOrderCancelled(o)) return; // skip cancelled orders entirely
      (o.items || []).forEach((i) => {
        const k = i.cancelled ? "Cancelled" : (i.current_step || "New");
        const qty = Number(i.units_total) || 1;
        c[k] = (c[k] || 0) + qty;
      });
    });
    return c;
  }, [orders]);

  const topScanners = useMemo(() => {
    const map = new Map();
    todaysEvents.forEach((e) => {
      map.set(e.user, (map.get(e.user) || 0) + (e.qty || 1));
    });
    return Array.from(map.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([name, count]) => ({ name, count }));
  }, [todaysEvents]);

  const recentTicker = useMemo(() =>
    events
      .slice()
      .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime())
      .slice(0, 8),
  [events]);

  const activeOrders = orders.filter((o) => !isOrderCompleted(o) && !isOrderCancelled(o)).length;
  const pace = Math.round((scansToday / Math.max(1, hoursSinceStartOfDay())) * 8); // projected 8-hr day

  // Today's fulfillment tally (delivery vs pickup) — derived from delivery_date.
  // Each bucket carries the orders themselves (not just a count) so the tile
  // can render order-number chips and completion status.
  const paintStats = useMemo(() => {
    let items = 0;
    let oldest = 0;
    orders.forEach((o) => {
      if (isOrderCancelled(o)) return;
      const c = Number(o.paint_out_count) || 0;
      if (c > 0) {
        items += c;
        const d = Number(o.paint_oldest_days_out) || 0;
        if (d > oldest) oldest = d;
      }
    });
    return { items, oldest };
  }, [orders]);

  const todaysFulfillment = useMemo(() => {
    const todayStr = new Date().toISOString().slice(0, 10);
    const deliveries = [];
    const pickups = [];
    orders.forEach((o) => {
      if (!o.delivery_date) return;
      if (isOrderCancelled(o)) return; // cancelled orders are never "scheduled"
      const d = String(o.delivery_date).slice(0, 10);
      if (d !== todayStr) return;
      if ((o.fulfillment_type || "").toLowerCase() === "pickup") pickups.push(o);
      else deliveries.push(o); // default to delivery
    });
    const completedCount = (list) => list.filter((o) => isOrderCompleted(o)).length;
    return {
      deliveries,
      pickups,
      deliveriesDone: completedCount(deliveries),
      pickupsDone: completedCount(pickups),
      total: deliveries.length + pickups.length,
    };
  }, [orders]);

  return (
    <div className="min-h-screen bg-[#0A0A0B] text-white overflow-hidden relative" data-testid="wallboard">
      {/* Grain overlay */}
      <div className="grain absolute inset-0 pointer-events-none" />

      {/* Top bar */}
      <header className="relative z-10 px-10 pt-8 pb-6 flex items-center justify-between border-b border-white/5">
        <div className="flex items-center gap-4">
          <CDLogo className="w-14 h-14 shrink-0" />
          <div>
            <div className="text-[11px] uppercase tracking-[0.35em] text-zinc-500 font-bold">Cabinet Depot · Live</div>
            <div className="font-heading font-black text-4xl leading-tight">Shop Floor Wallboard</div>
          </div>
          {viewMode === "test" && (
            <span className="ml-4 px-3 py-1.5 bg-yellow-400 text-yellow-950 text-[10px] uppercase tracking-[0.3em] font-black rounded">
              Test mode
            </span>
          )}
          <span
            className="ml-4 px-3 py-1.5 bg-white/5 border border-white/10 text-zinc-300 text-[10px] uppercase tracking-[0.3em] font-black rounded"
            data-testid="active-preset-badge"
          >
            {wb.activePreset}
          </span>
        </div>
        <div className="flex items-center gap-6">
          {config.showClock && (
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-bold">{formatDate(now)}</div>
              <div className="font-heading font-black text-5xl tabular-nums leading-none mt-1" data-testid="wallboard-clock">
                {formatTime(now)}
              </div>
            </div>
          )}
          <div className="flex flex-col items-end gap-1.5">
            <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] font-bold text-emerald-400">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              Live · {config.refreshSec < 60 ? `${config.refreshSec}s` : `${config.refreshSec / 60}m`}
            </span>
            <button
              onClick={() => setSettingsOpen(true)}
              data-testid="wallboard-settings-open"
              className="text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-500 hover:text-white transition-colors inline-flex items-center gap-1"
            >
              <GearSix size={12} weight="bold" /> Configure
            </button>
            <button
              onClick={goFullscreen}
              data-testid="wallboard-fullscreen"
              className="text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-500 hover:text-white transition-colors inline-flex items-center gap-1"
            >
              <ArrowSquareOut size={12} weight="bold" /> Fullscreen
            </button>
            <Link
              to="/"
              data-testid="wallboard-exit"
              className="text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-500 hover:text-white transition-colors inline-flex items-center gap-1"
            >
              <X size={12} weight="bold" /> Exit
            </Link>
          </div>
        </div>
      </header>

      {/* Goal ring (optional) */}
      {config.goalEnabled && (
        <section className="relative z-10 px-10 pt-8 flex items-center gap-8" data-testid="goal-section">
          <GoalRing current={scansToday} target={config.dailyGoal} />
          <div className="space-y-1.5">
            <div className="text-[10px] uppercase tracking-[0.35em] text-zinc-500 font-bold">Today's target</div>
            <div className="font-heading font-black text-3xl">
              {scansToday >= config.dailyGoal
                ? <span className="text-emerald-400">Goal hit · keep going</span>
                : <>
                    <span className="text-white">{Math.max(0, config.dailyGoal - scansToday)}</span>
                    <span className="text-zinc-500"> scans to go</span>
                  </>}
            </div>
            <div className="text-sm text-zinc-400">
              At current pace: <span className="font-mono font-bold text-white">{pace}</span> projected by 5pm
              {pace >= config.dailyGoal ? <span className="text-emerald-400"> · on track</span> : <span className="text-amber-400"> · need to pick up</span>}
            </div>
          </div>
        </section>
      )}

      {/* KPIs */}
      {config.showKpis && (
      <section className="relative z-10 px-10 pt-8 grid grid-cols-4 gap-6">
        <BigKpi
          label="Scans today"
          value={scansToday}
          accent="#C8102E"
          icon={Pulse}
          sub={`projected ${pace} by 5pm`}
          testId="kpi-scans-today"
        />
        <BigKpi
          label="Shipped today"
          value={shippedToday}
          accent="#10B981"
          icon={Truck}
          sub={shippedToday ? "out the door" : "let's get one out"}
          testId="kpi-shipped-today"
        />
        <BigKpi
          label="Active orders"
          value={activeOrders}
          accent="#3B82F6"
          icon={Package}
          sub={`${orders.length} total in system`}
          testId="kpi-active-orders"
        />
        <BigKpi
          label="Top scanner"
          value={topScanners[0]?.name || "—"}
          valueClass="text-3xl"
          accent="#F59E0B"
          icon={Trophy}
          sub={topScanners[0] ? `${topScanners[0].count} scans` : "be the first today"}
          testId="kpi-top-scanner"
        />
      </section>
      )}

      {/* Today's fulfillment tally */}
      {config.showFulfillmentTile !== false && todaysFulfillment.total > 0 && (
        <section className="relative z-10 px-10 pt-6" data-testid="fulfillment-tile">
          <div className="rounded-xl border border-white/5 bg-white/[0.03] px-6 py-4 space-y-3">
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-zinc-500">Scheduled today</div>
            {todaysFulfillment.deliveries.length > 0 && (
              <FulfillmentRow
                icon={Truck}
                iconColor="text-blue-400"
                label={todaysFulfillment.deliveries.length === 1 ? "delivery" : "deliveries"}
                orders={todaysFulfillment.deliveries}
                done={todaysFulfillment.deliveriesDone}
                testId="fulfillment-deliveries"
              />
            )}
            {todaysFulfillment.pickups.length > 0 && (
              <FulfillmentRow
                icon={Storefront}
                iconColor="text-emerald-400"
                label={todaysFulfillment.pickups.length === 1 ? "pickup" : "pickups"}
                orders={todaysFulfillment.pickups}
                done={todaysFulfillment.pickupsDone}
                testId="fulfillment-pickups"
              />
            )}
          </div>
        </section>
      )}

      {/* Paint queue tally */}
      {paintStats.items > 0 && (
        <section className="relative z-10 px-10 pt-6" data-testid="paint-tile">
          <div className="rounded-xl border border-purple-500/20 bg-purple-500/5 px-6 py-4 flex items-center gap-6 flex-wrap">
            <PaintBrush size={40} weight="bold" className="text-purple-300 shrink-0" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-purple-300/80">At painter</div>
              <div className="mt-1 flex items-baseline gap-6">
                <div>
                  <div className="font-heading font-black text-4xl tabular-nums leading-none text-purple-200">{paintStats.items}</div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-purple-300/70 font-bold mt-1">items</div>
                </div>
                {paintStats.oldest > 0 && (
                  <div>
                    <div className="font-heading font-black text-4xl tabular-nums leading-none text-purple-200">{paintStats.oldest}d</div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-purple-300/70 font-bold mt-1">oldest</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Stage breakdown + scoreboard + ticker */}
      <section className="relative z-10 px-10 pt-8 grid grid-cols-12 gap-6 pb-10">
        {/* Stage funnel */}
        {(config.showStageTiles || config.showPipeline) && (
        <div className="col-span-7 space-y-5">
          <SectionHeader title="Items by stage" />
          {config.showStageTiles && (
          <div className="grid grid-cols-4 gap-4">
            <StageTile label="New"      count={stageCounts.New}      accent="#71717A" icon={Package} />
            <StageTile label="Building" count={stageCounts.Building} accent="#3B82F6" icon={Hammer} />
            <StageTile label="QA"       count={stageCounts.QA}       accent="#EA580C" icon={ShieldCheck} />
            <StageTile label="Shipped"  count={stageCounts.Shipped}  accent="#10B981" icon={CheckCircle} />
          </div>
          )}

          {/* Pipeline bar */}
          {config.showPipeline && <PipelineBar counts={stageCounts} />}
        </div>
        )}

        {/* Today's leaderboard */}
        {config.showLeaderboard && (
        <div className={`${(config.showStageTiles || config.showPipeline) ? "col-span-5" : "col-span-12"} space-y-5`}>
          <SectionHeader title="Today's leaderboard" />
          <div className="rounded-lg bg-white/[0.03] border border-white/5 divide-y divide-white/5" data-testid="leaderboard-today">
            {topScanners.length === 0 ? (
              <div className="px-6 py-10 text-center text-zinc-500 text-sm">
                No scans yet today.
              </div>
            ) : (
              topScanners.map((u, i) => (
                <div key={u.name} className="px-6 py-4 flex items-center gap-4" data-testid={`wb-leader-${i}`}>
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center font-heading font-black text-base ${rankColor(i)}`}>
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate text-lg">{u.name}</div>
                    <div className="mt-1.5 h-1.5 rounded-full bg-white/5 overflow-hidden">
                      <div
                        className="h-full bg-[#C8102E]"
                        style={{ width: `${Math.max(4, Math.round((u.count / (topScanners[0]?.count || 1)) * 100))}%` }}
                      />
                    </div>
                  </div>
                  <div className="font-mono font-black text-3xl tabular-nums">{u.count}</div>
                </div>
              ))
            )}
          </div>
        </div>
        )}

        {/* Live ticker */}
        {config.showTicker && (
        <div className="col-span-12 space-y-3">
          <SectionHeader title="Live activity feed" />
          {recentTicker.length === 0 ? (
            <div className="rounded-lg bg-white/[0.03] border border-white/5 px-6 py-8 text-center text-zinc-500">
              Quiet on the floor — no recent scans.
            </div>
          ) : (
            <div className="rounded-lg bg-white/[0.03] border border-white/5 divide-y divide-white/5" data-testid="wb-ticker">
              {recentTicker.map((e, i) => (
                <div key={e.key || i} className="px-6 py-3 flex items-center gap-5">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold w-20 shrink-0 font-mono">
                    {relativeTime(e.at)}
                  </div>
                  <StagePill step={e.step} />
                  <div className="flex-1 min-w-0">
                    <span className="font-semibold">{e.user}</span>
                    <span className="text-zinc-500"> advanced </span>
                    <span className="font-mono text-zinc-300">{e.sku}</span>
                    <span className="text-zinc-500"> on </span>
                    <span className="font-semibold">{e.orderNumber}</span>
                    <span className="text-zinc-500"> · {e.itemName}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        )}
      </section>

      {/* Footer */}
      <footer className="relative z-10 px-10 py-3 border-t border-white/5 flex items-center justify-between text-[10px] uppercase tracking-[0.3em] font-bold text-zinc-600">
        <div>Auto-refresh · {lastUpdatedAt ? `last updated ${relativeTime(lastUpdatedAt)}` : "loading…"}</div>
        <div className="font-mono">Cabinet Depot · {new Date().getFullYear()}</div>
      </footer>

      <WallboardSettings
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        config={config}
        update={wb.update}
        resetActive={wb.resetActive}
        activePreset={wb.activePreset}
        presetNames={wb.presetNames}
        selectPreset={wb.selectPreset}
        createPreset={wb.createPreset}
        renamePreset={wb.renamePreset}
        deletePreset={wb.deletePreset}
      />
    </div>
  );
}

/* ---------- subcomponents ---------- */

function BigKpi({ label, value, sub, accent, icon: Icon, testId, valueClass = "" }) {
  return (
    <div
      className="rounded-xl border border-white/5 bg-white/[0.03] p-6 relative overflow-hidden"
      data-testid={testId}
    >
      <div
        className="absolute -right-6 -top-6 w-24 h-24 rounded-full blur-2xl opacity-30"
        style={{ background: accent }}
      />
      <div className="relative flex items-start justify-between">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-zinc-500">{label}</div>
          <div className={`mt-2 font-heading font-black tabular-nums truncate ${valueClass || "text-6xl"}`}>
            {value}
          </div>
          {sub && <div className="mt-1 text-xs text-zinc-500">{sub}</div>}
        </div>
        <div
          className="w-12 h-12 rounded-md flex items-center justify-center shrink-0"
          style={{ background: `${accent}22`, color: accent }}
        >
          <Icon size={26} weight="bold" />
        </div>
      </div>
    </div>
  );
}

function StageTile({ label, count, accent, icon: Icon }) {
  return (
    <div
      className="rounded-lg border border-white/5 bg-white/[0.03] p-5"
      data-testid={`stage-tile-${label.toLowerCase()}`}
    >
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-zinc-500">{label}</div>
        <Icon size={16} weight="bold" style={{ color: accent }} />
      </div>
      <div className="mt-2 font-heading font-black text-5xl tabular-nums" style={{ color: accent }}>
        {count}
      </div>
    </div>
  );
}

function SectionHeader({ title }) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-1 h-6 bg-[#C8102E] rounded" />
      <h2 className="font-heading font-black uppercase tracking-[0.2em] text-sm">{title}</h2>
    </div>
  );
}

function PipelineBar({ counts }) {
  const total = STEPS.reduce((s, k) => s + (counts[k] || 0), 0);
  if (!total) return null;
  const colors = { New: "#52525B", Building: "#3B82F6", QA: "#EA580C", Shipped: "#10B981" };
  return (
    <div data-testid="pipeline-bar" className="space-y-2">
      <div className="flex h-3 rounded-full overflow-hidden bg-white/5">
        {STEPS.map((s) => {
          const pct = ((counts[s] || 0) / total) * 100;
          if (!pct) return null;
          return <div key={s} style={{ width: `${pct}%`, background: colors[s] }} title={`${s}: ${counts[s]}`} />;
        })}
      </div>
      <div className="flex justify-between text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-500 font-mono">
        {STEPS.map((s) => (
          <span key={s}>
            <span style={{ color: colors[s] }}>●</span> {s} {counts[s] || 0}
          </span>
        ))}
      </div>
    </div>
  );
}

function StagePill({ step }) {
  const colors = {
    New: { bg: "#3F3F46", fg: "#E4E4E7" },
    Building: { bg: "#1E3A8A", fg: "#BFDBFE" },
    QA: { bg: "#7C2D12", fg: "#FED7AA" },
    Shipped: { bg: "#064E3B", fg: "#A7F3D0" },
  };
  const c = colors[step] || colors.New;
  return (
    <span
      className="inline-block px-2.5 py-1 rounded text-[10px] uppercase tracking-[0.2em] font-black w-24 text-center shrink-0"
      style={{ background: c.bg, color: c.fg }}
    >
      {step}
    </span>
  );
}

function FulfillmentRow({ icon: Icon, iconColor, label, orders, done, testId }) {
  return (
    <div className="flex items-start gap-4 flex-wrap" data-testid={testId}>
      <Icon size={28} weight="bold" className={`${iconColor} shrink-0 mt-1`} />
      <div className="shrink-0">
        <div className="font-heading font-black text-3xl tabular-nums leading-none">
          {orders.length}
        </div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold mt-0.5">
          {label} · <span className={done === orders.length ? "text-emerald-400" : "text-amber-400"}>{done}/{orders.length} done</span>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 flex-1 min-w-0">
        {orders.map((o) => {
          const completed = isOrderCompleted(o);
          return (
            <Link
              key={o.id}
              to={`/orders/${o.id}`}
              data-testid={`fulfillment-chip-${o.order_number}`}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] uppercase tracking-[0.15em] font-bold border transition-colors ${
                completed
                  ? "bg-emerald-950/40 border-emerald-700 text-emerald-300 hover:bg-emerald-900/40"
                  : "bg-white/5 border-white/15 text-zinc-200 hover:bg-white/10 hover:border-white/30"
              }`}
              title={[
                o.order_number,
                o.customer_name,
                completed ? "Completed" : "In progress",
              ].filter(Boolean).join(" · ")}
            >
              {completed && <CheckCircle size={11} weight="fill" />}
              {o.order_number}
            </Link>
          );
        })}
      </div>
    </div>
  );
}

/* ---------- helpers ---------- */

function startOfToday() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

function hoursSinceStartOfDay() {
  return (Date.now() - startOfToday().getTime()) / 3600000;
}

function flattenEvents(orders) {
  const out = [];
  (orders || []).forEach((order) => {
    (order.items || []).forEach((item) => {
      (item.progress || []).forEach((step) => {
        if (step.completed && step.last_timestamp && step.last_user_name) {
          // qty: how many physical units were advanced at this step. The
          // backend's progress[] entry exposes a `count` (units scanned so
          // far) and `total` (target). For a completed step those are
          // equal, but we fall back through count → total → 1 so a missing
          // field never silently zeroes out a real scan.
          const qty = Number(step.count ?? step.total ?? item.units_total ?? 1) || 1;
          out.push({
            key: `${item.id}-${step.step}`,
            user: step.last_user_name,
            step: step.step,
            at: step.last_timestamp,
            sku: item.sku,
            itemName: item.name,
            orderNumber: order.order_number,
            qty,
          });
        }
      });
    });
  });
  return out;
}

function relativeTime(at) {
  const ms = Date.now() - new Date(at).getTime();
  const sec = Math.floor(ms / 1000);
  if (sec < 10) return "now";
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  const day = Math.floor(hr / 24);
  return `${day}d`;
}

function formatTime(d) {
  return d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true });
}

function formatDate(d) {
  return d.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric", year: "numeric" });
}

function rankColor(i) {
  if (i === 0) return "bg-[#C8102E] text-white";
  if (i === 1) return "bg-amber-500/30 text-amber-200";
  if (i === 2) return "bg-zinc-700 text-zinc-300";
  return "bg-white/5 text-zinc-400";
}
